import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import { useScriptsPageContext } from "@contexts/main/scripts_page_context"
import { oneTimesUpdate } from "@domain/coupons"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import { Box, InputLabel, Stack, Typography } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider"
import { showErrorToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import dayjs from "dayjs"
import "dayjs/locale/tr"
import { useState } from "react"
import * as yup from "yup"

export type DeleteFormData = {
  customer: string
  startDate: Date
  endDate: Date
}

export default function DeletePersons({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate } = useScriptsPageContext()

  const schema = yup
    .object({
      customer: yup.string().required(),
      startDate: yup.date().required(),
      endDate: yup.date().required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty }
  } = useControlledForm<DeleteFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      startDate: new Date(),
      endDate: new Date()
    }
  })

  const [loading, setLoading] = useState(false)
  const [isStartDateOpen, setIsStartDateOpen] = useState(false)
  const [isEndDateOpen, setIsEndDateOpen] = useState(false)
  const [isDateFocus, setIsDateFocus] = useState(false)

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: DeleteFormData) {
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await oneTimesUpdate({
      type: OneTimesType.DeletePersons,
      metaData: {
        customer: data.customer,
        startDate: data.startDate,
        endDate: data.endDate
      }
    })
    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("delete_persons")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid || !isDirty
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<DeleteFormData>
          label={translate("customer_id")}
          disabled={loading}
          name="customer"
          placeholder={translate("customer_id")}
          control={control}
          required
        />
        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("start_date")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                format="DD.MM.YYYY"
                disableFuture
                disableOpenPicker
                open={isStartDateOpen}
                onClose={() => setIsStartDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setIsStartDateOpen(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("startDate", new Date())
                  }
                }}
                onChange={(date) => {
                  if (date && date.isValid()) {
                    const dateObj = date.toDate()
                    setValue("startDate", dateObj)
                  }
                }}
                defaultValue={dayjs(new Date())}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
        <Stack>
          <InputLabel
            sx={{
              color: isDateFocus ? PRIMARY : INPUT_LABEL_BLUR,
              alignItems: "center"
            }}
          >
            <Typography>{translate("end_date")}</Typography>
          </InputLabel>
          <Box
            sx={{
              border: "1px solid #DDDDDD",
              borderRadius: 1,
              ":hover": {
                border: "1px solid #000000"
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
              <DatePicker
                sx={{
                  "& input": { width: "100%" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none"
                    }
                  },
                  width: "100%"
                }}
                format="DD.MM.YYYY"
                disableFuture
                disableOpenPicker
                open={isEndDateOpen}
                onClose={() => setIsEndDateOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setIsEndDateOpen(true),
                    onFocus: () => setIsDateFocus(true),
                    onBlur: () => setIsDateFocus(false)
                  },
                  field: {
                    clearable: true,
                    onClear: () => setValue("endDate", new Date())
                  }
                }}
                onChange={(date) => {
                  if (date && date.isValid()) {
                    const dateObj = date.toDate()
                    setValue("endDate", dateObj)
                  }
                }}
                defaultValue={dayjs(new Date())}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </Stack>
    </DialogProvider>
  )
}
